goog.provide('el8.uiwidget.Feed');

goog.require('el8.model.base');
goog.require('el8.service.tz');
goog.require('el8.uiwidget.FeedPage');
goog.require('el8.uiwidget.FeedData');
goog.require('el8.uiwidget.FeedRenderer');
goog.require('el8.util.base');


/** ========================================================
 *   Basic UI Feed object
 *
 *   The main use case for a Feed is any list of objects
 *   that needs to be rendered, where each object has
 *   a unique ID. It typically makes sense to bring in this
 *   Feed infrastructure if you want to accommodate any sort
 *   of regular manipulation or updating of the list.
 *   It's also useful for paged loading.
 *
 *   It has a number of helper classes:
 *      - FeedRenderer: connects the feed to the DOM
 *      - FeedData: manage the index for a feed
 *      - FeedPage: a slice of the FeedData index
 *
 *   These are used all over the place and there are lots of
 *   subclasses. E.g., ChartFeed, reportFeeds, InboxFeed (doc mgr).
 * ======================================================== */
el8.uiwidget.Feed = function(spec) {

    spec = spec || {};
    var that = this;

    /**
     * Help with naming of specific items in the feed
     */
    this._name = spec.name;

    /**
     * For binding clicks
     */
    this._container = spec.container; // required
    this._clickTriggerMap = spec.clickTriggerMap || {};

    /**
     * For React-rendering integration, to be used by `FeedData`
     */
    if (spec.reactRenderMap) {
        spec.shouldFetchItemFn = function isItemNotReactRendered(indexedItem) {
            return !spec.reactRenderMap[indexedItem.model_class];
        }
    }

    /**
     * Core helper objects for the Feed
     */
    this._feedData = new el8.uiwidget.FeedData(spec);
    var rendererConstructor = spec.rendererConstructor || el8.uiwidget.FeedRenderer;
    this._feedRenderer = new rendererConstructor($.extend(spec, {
        parentFeed: this,
        feedData: this._feedData,
        triggerFn: function(var_args) { that.trigger.apply(that, arguments); }
    }));

    /**
     * Custom specs for this feed
     */
    this._urlIndex = spec.urlIndex;

    /**
     * preProcessIndexFn is useful when constructing a Feed from a subset of the info in
     * a URL that server is already sending down – rather than needing to a create a
     * new endpoint for every client feed, the client can just create subsets
     * of the index. It can also be used to add "objects" to a feed that only
     * the client knows about.
     *
     * For an example usage, see the feeds in the Letters Attachment UI
     * (attachmentGroups and selectAttachments.js)
     */
    this._preProcessIndexFn = spec.preProcessIndexFn || null;

    /***************
     * Holds state *
     ***************/
    this._needsUpdate = false;  // Does the feed need to be updated on next show?
    this._isFrozen = false;     // Is the feed frozen from receiving updates?
    this._isInit = false;       // Is the feed initialized?

};

//========================================================
// Give ability to bind/trigger events
//========================================================
el8.uiwidget.Feed.addEvents();


//========================================================
// Initialize feed
//========================================================
el8.uiwidget.Feed.prototype.init = function() {

    var that = this;
    this._isInit = true;

    this._feedRenderer.init({
        moreTriggerFn: function() { that._showMore(); },
        checkMarkerFn: function(container, itemId) {
            return that._checkMarker(container, itemId);
        }
    });

    /**
     * Bind all the click triggers here, so that the callback's can access
     * the Feed itself
     */
    var genClickFn = function(t, fn) {
        if (fn) {
            return function(e) {
                var retVal = fn($(e.target).closest(t), that);
                // Return value from fn, if available
                if (retVal !== undefined) { return retVal; }
                // Else, propagate
                return true;
            };
        } else {
            return function(e) {
                el8.uiwidget.alert('This feature has not yet been implemented');
                return true;
            };
        }
    };
    for (var t in that._clickTriggerMap) {
        if (that._clickTriggerMap.hasOwnProperty(t)) {
            $(this._container).on('click', t, genClickFn(t, that._clickTriggerMap[t]));
        }
    }

    return this;
};


//========================================================
// Useful public methods
//========================================================
el8.uiwidget.Feed.prototype.loadIndex = function(index) {
    var alteredIndex = this._preProcessIndexFn ?
        this._preProcessIndexFn(index) : index;
    this._feedData.loadIndex(alteredIndex);
    this._needsUpdate = false;
    this._feedRenderer.markNeedsRender();
    this.trigger('changeIndex');
};
el8.uiwidget.Feed.prototype.getIndexSize = function() {
    return this._feedData._getIndexSize();
};
el8.uiwidget.Feed.prototype.getIndex = function() {
    return this._feedData.getDataIndex();
};
el8.uiwidget.Feed.prototype.getListElt = function() {
    return this._feedRenderer.getListElt();
};
el8.uiwidget.Feed.prototype.getRenderItem = function(itemId) {
    return this._feedRenderer.getRenderItem(itemId);
};
el8.uiwidget.Feed.prototype.hide = function(duration) {
    return this._feedRenderer.hide(duration);
};
el8.uiwidget.Feed.prototype.destroy = function(duration) {
    return this._feedRenderer.destroy(duration);
};
el8.uiwidget.Feed.prototype.getFeedId = function(itemId) {
    return this._feedRenderer.getFeedId(itemId);
};
el8.uiwidget.Feed.prototype.hasItem = function(itemId) {
    return this._feedData.hasItem(itemId);
};
el8.uiwidget.Feed.prototype.resetRender = function() {
    this._feedRenderer.reset();
    return this;
};
el8.uiwidget.Feed.prototype.isRendered = function() {
    return this._feedRenderer.isRendered();
};
el8.uiwidget.Feed.prototype.isIndexLoaded = function() {
    return this._feedData.isIndexLoaded();
};
el8.uiwidget.Feed.prototype.markNeedsUpdate = function() {
    this._needsUpdate = true;
    return this;
};
el8.uiwidget.Feed.prototype.freeze = function() {
    this._isFrozen = true;
    return this;
};
el8.uiwidget.Feed.prototype.unfreeze = function() {
    if (this._isFrozen) {
        this._isFrozen = false;
        this.display(); // show any updates we suppressed
    }
    return this;
};
el8.uiwidget.Feed.prototype.highlight = function(itemId) {
    this._feedRenderer.highlight(itemId);
    return this;
};
el8.uiwidget.Feed.prototype.unhighlight = function(itemId) {
    this._feedRenderer.unhighlight(itemId);
    return this;
};
el8.uiwidget.Feed.prototype.isEmpty = function() {
    return this._feedData.isEmpty();
};
el8.uiwidget.Feed.prototype.scrollToTop = function() {
    this._feedRenderer.scrollToTop();
};


//========================================================
// Methods to show, refresh, remove specific items
//========================================================
el8.uiwidget.Feed.prototype.remove = function(itemId, duration) {
    if (itemId instanceof el8_Entity) { // supports itemId as item
        itemId = itemId.getId();
    }
    var retVal = this._feedRenderer.removeRenderItem(itemId, duration);
    // Awkward retVal is so we can trigger event only after event is complete
    this.trigger('changeIndex');
    return retVal;
};
el8.uiwidget.Feed.prototype.refreshItem = function(itemId, force) {
    if (itemId instanceof el8_Entity) { // supports itemId as item
        itemId = itemId.getId();
    }
    this._feedRenderer.refreshRenderItem(itemId, force);
};
el8.uiwidget.Feed.prototype.showItem = function(itemId) {

    if (itemId instanceof el8_Entity) { // supports itemId as item
        itemId = itemId.getId();
    }

    // Synchronous call to show feed
    this.display({async: false});

    // Make sure this is in the index
    if (this._feedData.hasItem(itemId)) {

        // If item is not yet showing in feed, make it show
        if (!this._feedRenderer.isItemRendered(itemId)) {
            this._showMore(itemId);
        }
        this._feedRenderer.highlight(itemId);
        this._feedRenderer.scrollToItem(itemId);

        var that = this;
        var removeHighlight = function() { that._feedRenderer.unhighlight(itemId); };
        $.el8_setTimeout(removeHighlight, 15*1000); // remove highlight after 15 sec
    }

};


//========================================================
// Methods to render by page
//========================================================
el8.uiwidget.Feed.prototype._showFirstPage = function(opts) {
    return this._feedRenderer.renderPageByNumber(0, opts);
};
el8.uiwidget.Feed.prototype._showMore = function(id) {
    return id ? this._feedRenderer.appendNextUntilId(id) :
        this._feedRenderer.appendNextPage();
};
el8.uiwidget.Feed.prototype.showPageByDate = function(d8) {
    return this._feedRenderer.renderPageByDate(d8);
};
// be careful with this function: it might be really slow
// for a big Feed
el8.uiwidget.Feed.prototype.showAllPages = function() {
    return this._feedRenderer.appendAllItems();
};

// Check if marker is visible within the application view.
el8.uiwidget.Feed.prototype._checkMarker = function(container, itemId) {
    return this._feedRenderer.isVisible(container, itemId);
};

//========================================================
// Main methods: show feed, update feed
//========================================================
el8.uiwidget.Feed.prototype.display = function(opts) {

    /**
     * This method is called when we want the feed to be displayed and do not
     * care to fetch again from the server if the index is already loaded.
     *
     * It is lazy about re-fetching the index and only does so if the
     * index has not been loaded yet, or if the feed has been marked as
     * needing updating.
     */

    opts = opts || {};

    if (!this._isInit) { this.init(); }

    /* In the very least, make sure container is showing */
    this._feedRenderer.showContainer();

    if (!this._feedData.isIndexLoaded()) {
        // If this has never loaded index, then call refresh() so that
        // user gets explicit visual feedback that feed is being loaded
        this.refresh(opts);

    } else if (this._needsUpdate) {

        if (!this.isRendered()) {
            // If feed is not rendered, then call refresh() so that
            // user gets explicit visual feedback that feed is being loaded
            this.refresh(opts);
        } else {
            // Else, if feed has already been rendered and needs updating,
            // try to do it in a non visually intrusive manner
            this.update(opts);
        }

    } else if (this._feedRenderer.getNeedsRender()) {
        // This is like a refresh without the refetch
        this.resetRender(); // this is "refresh" part
        this._showFirstPage(); // and since we just reset the renderedPage, we never want refreshAfterUpdate

    } else {
        if (!this._feedRenderer.countRenderedItems()) {
            // If nothing showing, then either this is the first time rendering, or
            // the feed is empty. Either way, safest to show first page
            this._showFirstPage();
        } // Else if already showing items, no need to do anything else
        if (opts.successCb) { opts.successCb(); }
    }

    return this;
};
el8.uiwidget.Feed.prototype.update = function(opts) {

    /**
     * This method is called when we want to make sure to re-fetch the index and
     * re-render any changes as needed. This method can also be called to do the initial
     * display of the feed, particularly if we want to make sure the index gets re-fetched.
     *
     * The typical use case is to call this method in the background, and its intended
     * to be as non-disruptive as possible to the user.
     */

    if (!this._isInit) { this.init(); }

    if (this._isFrozen) {
        // Do nothing and just mark as needing update if feed is frozen
        this.markNeedsUpdate();
        return;
    }

    this._fetchAndShow(opts);

    return this;
};
el8.uiwidget.Feed.prototype.refresh = function(opts) {

    /**
     * This method is called on a feed when we want to re-fetch the index and
     * render the existing page fresh. This method can also be called to do
     * the initial display of the feed, particularly when we want to make it
     * visually explicit to the user (as opposed to update()).
     *
     * refresh() is intended to be visually explicit to the user (unlike update())
     */

    opts = opts || {};
    var async = opts.async;
    var newestFirst = opts.newestFirst;
    var preRenderCb = opts.preRenderCb;
    var successCb = opts.successCb;
    var errorCb = opts.errorCb;

    if (!this._isInit) { this.init(); }

    this.resetRender();
    this._feedRenderer.displayLoading();

    var that = this;
    this._fetchAndShow({
        async: async,
        newestFirst: newestFirst,
        preRenderCb: function() {
            // We run this as part of the fetchDataIndex successCb before
            // any feed rendering happens. Because those can involve server
            // calls that manage their own loading symbol displays
            that._feedRenderer.clearLoading();
            that._feedRenderer.show(); // does just a simple show of the dom elts
            if (preRenderCb) { preRenderCb(); }
        },
        successCb: function(data) {
            that._feedRenderer.scrollToTop();
            if (successCb) { successCb(data); }
        },
        errorCb: function() {
            that._feedRenderer.clearLoading();
            if (errorCb) { errorCb(); }
        }
    });

    return this;
};
el8.uiwidget.Feed.prototype._fetchAndShow = function(opts) {

    var opts = opts || {};
    var oldIndex = this._feedData.getDataIndex();

    var that = this;
    this.fetchDataIndex({
        async: opts.async,
        newestFirst: opts.newestFirst,
        successCb: function(data) {
            if (opts.preRenderCb) { opts.preRenderCb(); }
            if (!that._feedRenderer.countRenderedItems()) {
                // If nothing showing, then either this is the first time rendering, or
                // the feed is empty. Either way, safest to show first page
                that._showFirstPage();
            } else {
                that._feedRenderer.refreshAfterUpdate(oldIndex);
            }
            if (opts.successCb) { opts.successCb(); }
        },
        errorCb: function(data) {
            if (opts.errorCb) { opts.errorCb(); }
            that._feedRenderer.renderError();
        }
    });

};
el8.uiwidget.Feed.prototype.fetchDataIndex = function(opts) {

    /**
     * This method is called to refresh just the index for a feed,
     * without updating the rendered data.
     *
     * It is used internally to update a feed's index before it is re-rendered,
     * but it can also be called externally to refresh a feed in the background.
     * This is sometimes useful when the hidden feed still has a visible count.
     *
     * If you call fetchDataIndex, it will make sure that the feed is re-rendered
     * the next time it is displayed.
     */
    if (!this._urlIndex) { return; }

    opts = opts || {};

    var that = this;
    var urlParams = '';

    if (opts.hasOwnProperty('newestFirst')) {
        urlParams += (urlParams.indexOf('?') === -1) ? '?newest_first=' : '&newest_first=';
        urlParams += opts.newestFirst ? 1 : 0;
    }

    $.el8_ajax({
        url: that._urlIndex + urlParams,
        type: "GET",
        async: opts.async,
        success: function(data) {
            that.loadIndex(data);
            if (opts.successCb) { opts.successCb(data); }
        },
        error_override: function() {
            that.loadIndex([]);
            if (opts.errorCb) { opts.errorCb(); }
        }
    });
};
